import React from 'react';
import type { ApolloError } from 'apollo-client';
import { ExperienceSuccessTracker as ViewExperienceSuccessTracker } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracker-consumer/result-declared/index.tsx';
import { ExperienceTracker as ViewExperienceTracker } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracker-consumer/tracker-base/index.tsx';
import OpTrigger from '@atlassian/jira-common-util-op-triggers/src/on-mount';
import { useIntl } from '@atlassian/jira-intl';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import SubmitApdex from '@atlassian/jira-providers-spa-apdex-analytics/src/submit-apdex-mark/index.tsx';
import { isKnownSprintError } from '@atlassian/jira-software-burn-reports-common/src/common/utils/errors/index.tsx';
import Controls from '@atlassian/jira-software-burn-reports-common/src/ui/controls/index.tsx';
import { SprintDetailsLegacy } from '@atlassian/jira-software-burn-reports-common/src/ui/sprint-details/index.tsx';
import { ESTIMATION_ORIGINAL_ESTIMATE } from '@atlassian/jira-software-report-charting-common/src/common/utils/format-estimate/index.tsx';
import { BURNUP_REPORT } from '@atlassian/jira-software-report-charting-common/src/ui/error-empty-state/constants.tsx';
import ErrorEmptyState from '@atlassian/jira-software-report-charting-common/src/ui/error-empty-state/index.tsx';
import Spinner from '@atlassian/jira-software-report-charting-common/src/ui/spinner/index.tsx';
import responseToSla from '@atlassian/jira-software-sla-tracker/src/services/response-to-sla/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import type {
	BurnupChartData,
	SprintReportsFilters,
	SprintReportsEstimationStatistic,
	Sprint,
} from '../common/types';
import { useBurnupFilters } from '../controllers';
import { isErrorTrackingEnabledWithSentry, isPollinatorTenant } from '../feature-flags';
import { burnupChartPageLoad } from '../services/utils/performance-analytics';
import Chart from './chart';
import Grid from './grid';
import { getTrackEventContextAttributes, convertChartsDataSecondsToHours } from './utils';

export type Props = {
	now: Date | null;
	boardId: number;
	// Filters query data
	filters: SprintReportsFilters | null;
	filtersError: ApolloError | null;
	filtersLoading: boolean;
	estimationType: SprintReportsEstimationStatistic;
	// Chart query data
	chart: BurnupChartData | null;
	chartError: ApolloError | null;
	chartLoading: boolean;
	slaTrackingEnabled?: boolean;
	timeTrackingOptions: TimeTrackingOptions;
	// Refetch handlers
	onRefetchFilters: () => void;
	onRefetchChart: () => void;
};

const BurnupReportView = ({
	now,
	filters,
	filtersError,
	filtersLoading,
	estimationType,
	onRefetchFilters,
	chart,
	chartError,
	chartLoading,
	timeTrackingOptions,
	onRefetchChart,
	boardId,
	slaTrackingEnabled = false,
}: Props) => {
	const intl = useIntl();
	const [state, actions] = useBurnupFilters();

	const onEditSprintComplete = (sprint: Sprint) => {
		actions.setSprintAndDirty(sprint);
		onRefetchFilters();
	};

	if (isKnownSprintError(chartError || filtersError)) {
		return (
			<>
				{slaTrackingEnabled && <ViewExperienceSuccessTracker location="BurnupEmptyView" />}
				{!state.dataLoadedOnce && chart && <OpTrigger op={() => actions.setDataLoadedOnce(true)} />}
				{state.dataLoadedOnce && (
					<SubmitApdex
						ttrSlo={8000}
						ttiSlo={8000}
						appName="software-reports-sprint-burnup-chart"
						extra={{
							isSentryEnabled: String(isErrorTrackingEnabledWithSentry()),
							synthetic: isPollinatorTenant(),
						}}
						// @ts-expect-error - TS2769 - No overload matches this call.
						metric={burnupChartPageLoad}
					/>
				)}
			</>
		);
	}

	if (chartError || filtersError) {
		return (
			<>
				{slaTrackingEnabled &&
					responseToSla(
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						chartError || (filtersError as ApolloError),
						chartError ? 'burnupChart' : 'burnupFilters',
					)}
				<ErrorEmptyState
					reportName={BURNUP_REPORT}
					chartError={chartError}
					filtersError={filtersError}
					onClickRetry={chartError ? onRefetchChart : onRefetchFilters}
				/>
			</>
		);
	}

	const userTimeZone = chart && chart.timeZone ? chart.timeZone : 'Etc/UTC';

	return (
		<>
			{!filtersLoading && (
				<OpTrigger op={() => actions.selectFiltersOnDataLoaded(filters, estimationType)} />
			)}
			{!state.dataLoadedOnce && chart && <OpTrigger op={() => actions.setDataLoadedOnce(true)} />}
			{state.dataLoadedOnce && (
				<SubmitApdex
					ttrSlo={8000}
					ttiSlo={8000}
					appName="software-reports-sprint-burnup-chart"
					extra={{
						isSentryEnabled: String(isErrorTrackingEnabledWithSentry()),
						synthetic: isPollinatorTenant(),
					}}
					// @ts-expect-error - TS2769 - No overload matches this call.
					metric={burnupChartPageLoad}
				/>
			)}
			{slaTrackingEnabled ? (
				<ViewExperienceTracker location="BurnupFilters">
					{({ onExperienceSuccess }) => (
						<Controls
							disabled={false}
							sprintsLoading={filtersLoading}
							estimationFieldsLoading={filtersLoading}
							sprints={filters ? filters.sprints : []}
							sprint={state.sprint}
							onChangeSprint={actions.setSprintAndDirty}
							estimationFields={filters ? filters.estimationStatistic : []}
							onChangeEstimation={actions.setEstimationFieldAndDirty}
							estimation={state.estimation}
							onExperienceSuccess={onExperienceSuccess}
							boardId={boardId}
							onEditSprintComplete={onEditSprintComplete}
						/>
					)}
				</ViewExperienceTracker>
			) : (
				<Controls
					disabled={false}
					sprintsLoading={filtersLoading}
					estimationFieldsLoading={filtersLoading}
					sprints={filters ? filters.sprints : []}
					sprint={state.sprint}
					onChangeSprint={actions.setSprintAndDirty}
					estimationFields={filters ? filters.estimationStatistic : []}
					onChangeEstimation={actions.setEstimationFieldAndDirty}
					estimation={state.estimation}
					// Replace with lodash/noop
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					onExperienceSuccess={() => {}}
					boardId={1}
					onEditSprintComplete={onEditSprintComplete}
				/>
			)}

			<SprintDetailsLegacy sprint={state.sprint} intl={intl} />

			{slaTrackingEnabled ? (
				<>
					{(!chart || chartLoading) && (
						<Spinner testId="software-burnup-report.ui.burnup-spinner" />
					)}
					<ContextualAnalyticsData attributes={getTrackEventContextAttributes(state, filters)}>
						<ViewExperienceTracker location="BurnupChart">
							{({ onExperienceSuccess }) =>
								chart && !chartLoading ? (
									<Chart
										now={now}
										statisticField={state.estimation}
										burnup={
											state.estimation === ESTIMATION_ORIGINAL_ESTIMATE
												? convertChartsDataSecondsToHours(chart)
												: chart
										}
										userTimeZone={userTimeZone}
										timeTrackingOptions={timeTrackingOptions}
										onExperienceSuccess={onExperienceSuccess}
									/>
								) : null
							}
						</ViewExperienceTracker>
						<ViewExperienceTracker location="BurnupGrid">
							{({ onExperienceSuccess }) =>
								chart && !chartLoading ? (
									<Grid
										burnup={chart}
										statisticField={state.estimation}
										timeTrackingOptions={timeTrackingOptions}
										onExperienceSuccess={onExperienceSuccess}
									/>
								) : null
							}
						</ViewExperienceTracker>
					</ContextualAnalyticsData>
					<SpaStatePageReady />
				</>
			) : (
				<>
					{!chart || chartLoading || filtersLoading ? (
						<Spinner testId="software-burnup-report.ui.burnup-spinner" />
					) : (
						<>
							<ContextualAnalyticsData attributes={getTrackEventContextAttributes(state, filters)}>
								<Chart
									now={now}
									statisticField={state.estimation}
									burnup={
										state.estimation === ESTIMATION_ORIGINAL_ESTIMATE
											? convertChartsDataSecondsToHours(chart)
											: chart
									}
									userTimeZone={userTimeZone}
									timeTrackingOptions={timeTrackingOptions}
									// Replace with lodash/noop
									// eslint-disable-next-line @typescript-eslint/no-empty-function
									onExperienceSuccess={() => {}}
								/>
								<Grid
									burnup={chart}
									statisticField={state.estimation}
									timeTrackingOptions={timeTrackingOptions}
								/>
							</ContextualAnalyticsData>
							<SpaStatePageReady />
						</>
					)}
				</>
			)}
		</>
	);
};

export default BurnupReportView;
